import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hubAPI } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Rocket, Target, Layers, Sparkles, FolderKanban, CalendarClock, Users2, Flame, Trophy, Skull, Filter, Search, MessageSquare, Info, Pencil, Check, X, Route, Trash2, BookMarked } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Hint, InfoHint } from '@/components/common/Hint';
import { CreationCapBlockedDialog } from '@/components/hub/CreationCapBlockedDialog';
import { PipelineEditorDialog } from '@/components/hub/PipelineEditorDialog';
import { ThemeManagerDialog } from '@/components/hub/ThemeManagerDialog';
import { MyTemplatesDialog } from '@/components/hub/MyTemplatesDialog';
import { ThemeIcon } from '@/lib/themeIcons';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ICON_MAP = { rocket: Rocket, target: Target, layers: Layers, sparkles: Sparkles, folder: FolderKanban };

const daysLeft = (iso) => {
    if (!iso) return null;
    const diff = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
    return diff;
};

const daysSince = (iso) => {
    if (!iso) return null;
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return Number.isFinite(d) ? d : null;
};

/**
 * Calcule l'état d'une étape par rapport à sa durée max autorisée.
 * Retourne : { max_days, days_on_stage, over_days } ou null si pas d'échéance.
 */
const computeStageTimeliness = (project, theme) => {
    if (!project || !theme) return null;
    const stage = (theme.pipeline || []).find((s) => s.id === project.current_stage_id);
    if (!stage || !stage.max_days) return null;
    const daysOn = daysSince(project.stage_changed_at || project.created_at);
    if (daysOn == null) return null;
    return {
        max_days: stage.max_days,
        days_on_stage: daysOn,
        over_days: Math.max(0, daysOn - stage.max_days),
        days_left_on_stage: stage.max_days - daysOn,
    };
};

const importanceColor = (n) => {
    if (n >= 8) return { bg: 'bg-red-100', text: 'text-red-700', label: 'Critique' };
    if (n >= 5) return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Haute' };
    if (n >= 3) return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Moyenne' };
    return { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Basse' };
};

// Format relatif court : "à l'instant", "5m", "2h", "3j", puis date courte au-delà.
const formatRelativeShort = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now - d;
        const min = Math.floor(diffMs / 60000);
        if (min < 1) return "à l'instant";
        if (min < 60) return `${min}m`;
        const h = Math.floor(min / 60);
        if (h < 24) return `${h}h`;
        const days = Math.floor(h / 24);
        if (days < 7) return `${days}j`;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    } catch { return ''; }
};


export default function HubSpace() {
    const { spaceId } = useParams();
    const navigate = useNavigate();
    const [space, setSpace] = useState(null);
    const [themes, setThemes] = useState([]);
    const [projects, setProjects] = useState([]);
    const [access, setAccess] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [capBlocked, setCapBlocked] = useState(null); // limitInfo dict when blocked
    const [showClosed, setShowClosed] = useState(false);
    const [search, setSearch] = useState('');
    const [themeMgrOpen, setThemeMgrOpen] = useState(false);
    const [myTemplatesOpen, setMyTemplatesOpen] = useState(false);
    // Thème pré-sélectionné lorsqu'on clique "+ Nouveau projet" depuis une section thème
    const [preselectThemeId, setPreselectThemeId] = useState(null);
    // Filtres par thème (chips toggle) — null = tous
    const [themeFilters, setThemeFilters] = useState(new Set());
    // Vue kanban temporaire pour un thème (bouton dédié par thème)
    const [kanbanFor, setKanbanFor] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [spacesR, themesR, projectsR, accessR, membersR] = await Promise.all([
                hubAPI.listSpaces(),
                // Ne récupérer QUE les thèmes rattachés à cet espace de travail (les autres
                // ne doivent pas être proposés à la création d'un projet ici).
                hubAPI.listThemes({ space_id: spaceId }),
                hubAPI.listProjects(spaceId, { status: showClosed ? 'closed' : 'active' }),
                hubAPI.access(),
                hubAPI.members().catch(() => ({ data: [] })),
            ]);
            const sp = (spacesR.data || []).find((s) => s.id === spaceId);
            setSpace(sp);
            setThemes(themesR.data);
            setProjects(projectsR.data);
            setAccess(accessR.data);
            setMembers(membersR.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, [spaceId, showClosed]);

    useEffect(() => { load(); }, [load]);

    // Projets utilisés dans cet espace, groupés par thème
    const projectsByTheme = useMemo(() => {
        const q = (search || '').toLowerCase();
        const filtered = projects.filter((p) => !q || (p.name || '').toLowerCase().includes(q));
        const grouped = {};
        for (const t of themes) grouped[t.id] = [];
        // Projets orphelins (thème supprimé) rangés dans un bucket spécial
        grouped._orphan = [];
        for (const p of filtered) {
            if (grouped[p.theme_id]) grouped[p.theme_id].push(p);
            else grouped._orphan.push(p);
        }
        return grouped;
    }, [projects, themes, search]);

    // Thèmes ayant au moins 1 projet (utilisés) — sont affichés d'abord
    const usedThemes = useMemo(
        () => themes.filter((t) => (projectsByTheme[t.id] || []).length > 0),
        [themes, projectsByTheme]
    );
    const emptyThemes = useMemo(
        () => themes.filter((t) => (projectsByTheme[t.id] || []).length === 0),
        [themes, projectsByTheme]
    );
    const totalVisible = useMemo(
        () => Object.values(projectsByTheme).reduce((n, arr) => n + arr.length, 0),
        [projectsByTheme]
    );

    const canCreate = access?.can_create_projects;
    const isAdmin = access?.is_admin;
    const isAdminFull = access?.is_admin_full;

    // Suppression de projet (admin OU admin_full — Hub-scoped, moins strict que les prospects)
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [deletingProject, setDeletingProject] = useState(false);
    const handleDeleteProject = async () => {
        if (!projectToDelete) return;
        setDeletingProject(true);
        try {
            await hubAPI.deleteProject(projectToDelete.id);
            toast.success('Projet supprimé');
            setProjectToDelete(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de suppression');
        } finally {
            setDeletingProject(false);
        }
    };
    // onDelete passé aux ProjectCard : visible pour tout admin (Hub)
    const projectDeleteHandler = isAdmin ? ((project) => setProjectToDelete(project)) : undefined;

    const handleClickNewProject = async () => {
        // Vérifie le plafond de création avant d'ouvrir le dialog
        try {
            const r = await hubAPI.creationLimit();
            if (!r.data.can_create) {
                setCapBlocked(r.data);
                return;
            }
            setCreating(true);
        } catch {
            setCreating(true); // fallback : ouvrir le dialog quoi qu'il arrive
        }
    };

    if (loading) {
        return <DashboardLayout><div className="p-6 text-sm text-slate-500" data-testid="hubspace-loading">Chargement…</div></DashboardLayout>;
    }
    if (!space) {
        return (
            <DashboardLayout>
                <div className="p-6">
                    <Button variant="outline" onClick={() => navigate('/projets')} className="gap-2"><ArrowLeft className="w-4 h-4" /> Retour</Button>
                    <div className="mt-6 text-slate-500 text-sm">Espace introuvable.</div>
                </div>
            </DashboardLayout>
        );
    }

    const SpaceIcon = ICON_MAP[space.icon] || Rocket;

    return (
        <DashboardLayout>
        <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-5" data-testid="hub-space-page">
            <div>
                <button onClick={() => navigate('/projets')} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2" data-testid="btn-back-hub">
                    <ArrowLeft className="w-3.5 h-3.5" /> Retour aux espaces
                </button>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${space.color}20` }}>
                            <SpaceIcon className="w-7 h-7" style={{ color: space.color }} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">{space.name}</h1>
                        </div>
                    </div>
                    {canCreate && (
                        <Hint text="Crée un nouveau projet dans cet espace. Choisis un thème (qui définit le pipeline), une importance, une deadline et les membres impliqués. Plafond : 10 projets actifs par utilisateur." side="left">
                            <Button onClick={handleClickNewProject} className="gap-2" data-testid="btn-new-project">
                                <Plus className="w-4 h-4" /> Nouveau projet
                            </Button>
                        </Hint>
                    )}
                </div>
            </div>

            {/* Encart "À propos de cet espace" (visible par tous, éditable par admin) */}
            <SpaceDescriptionCard space={space} canEdit={isAdmin} onSaved={(updated) => setSpace(updated)} />

            {/* Barre d'actions : recherche + gestion thèmes + toggle clôturés */}
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <InfoHint text="Toutes les cartes projets sont affichées ensemble avec leur thème identifié par une pastille colorée. Utilisez les filtres pour ne voir qu'un thème, ou la vue kanban pour un thème donné." side="right" />
                    <span data-testid="project-count-total">
                        {totalVisible} projet{totalVisible > 1 ? 's' : ''} affichés
                        {themes.length > 0 && ` · ${themes.length} thème${themes.length > 1 ? 's' : ''}`}
                    </span>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        <Input
                            placeholder="Rechercher un projet…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 h-9 w-56"
                            data-testid="hub-search"
                        />
                    </div>
                    {isAdmin && (
                        <Hint text="Ajouter, modifier ou supprimer des thèmes. Chaque thème a son propre pipeline personnalisable.">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setThemeMgrOpen(true)}
                                className="gap-1.5"
                                data-testid="btn-manage-themes"
                            >
                                <Layers className="w-4 h-4" /> Gérer les thèmes
                            </Button>
                        </Hint>
                    )}
                    <Hint text="Renommer ou supprimer les modèles de pipeline que tu as enregistrés depuis un projet.">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMyTemplatesOpen(true)}
                            className="gap-1.5"
                            data-testid="btn-my-templates"
                        >
                            <BookMarked className="w-4 h-4" /> Mes modèles
                        </Button>
                    </Hint>
                    <Hint text={showClosed ? "Actuellement : projets clôturés. Cliquez pour revenir aux actifs." : "Actuellement : projets actifs. Cliquez pour voir les clôturés."}>
                        <Button
                            variant={showClosed ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setShowClosed(!showClosed)}
                            className="gap-1.5"
                            data-testid="toggle-closed"
                        >
                            <Filter className="w-4 h-4" />
                            {showClosed ? 'Clôturés' : 'Actifs'}
                        </Button>
                    </Hint>
                </div>
            </div>

            {/* Chips de filtre par thème — cliquer pour ne voir que ce thème (toggle) */}
            {themes.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center" data-testid="theme-filter-chips">
                    <button
                        onClick={() => setThemeFilters(new Set())}
                        className={cn(
                            'text-xs px-3 py-1.5 rounded-full border transition-all',
                            themeFilters.size === 0
                                ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                        )}
                        data-testid="filter-all"
                    >
                        Tous les thèmes ({totalVisible})
                    </button>
                    {themes.map((t) => {
                        const count = (projectsByTheme[t.id] || []).length;
                        const active = themeFilters.has(t.id);
                        return (
                            <button
                                key={t.id}
                                onClick={() => {
                                    setThemeFilters((prev) => {
                                        const nx = new Set(prev);
                                        if (nx.has(t.id)) nx.delete(t.id); else nx.add(t.id);
                                        return nx;
                                    });
                                }}
                                className={cn(
                                    'text-xs px-3 py-1.5 rounded-full border transition-all flex items-center gap-1.5',
                                    active
                                        ? 'text-white border-transparent shadow-sm'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300',
                                )}
                                style={active ? { backgroundColor: t.color } : undefined}
                                data-testid={`filter-theme-${t.id}`}
                            >
                                <ThemeIcon icon={t.icon} className="w-3.5 h-3.5" color={active ? '#fff' : t.color} />
                                <span>{t.name}</span>
                                <span className={cn('text-[10px] px-1.5 py-0 rounded-full', active ? 'bg-white/20' : 'bg-slate-100 text-slate-500')}>
                                    {count}
                                </span>
                                <Hint text="Ouvrir la vue kanban pour ce thème (par étapes du pipeline)">
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); setKanbanFor(t); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setKanbanFor(t); } }}
                                        className={cn('ml-1 rounded p-0.5 -mr-1 inline-flex', active ? 'hover:bg-white/20' : 'hover:bg-slate-100')}
                                        data-testid={`btn-kanban-${t.id}`}
                                    >
                                        <FolderKanban className="w-3 h-3" />
                                    </span>
                                </Hint>
                            </button>
                        );
                    })}
                    {isAdmin && emptyThemes.length > 0 && (
                        <span className="text-[11px] text-slate-400 italic ml-2">
                            {emptyThemes.length} thème{emptyThemes.length > 1 ? 's' : ''} sans projet — visibles dans « Gérer les thèmes »
                        </span>
                    )}
                </div>
            )}

            {/* Grille de projets — TOUS visibles, thème identifié sur chaque carte */}
            {themes.length === 0 ? (
                <Card><CardContent className="p-6 text-slate-500 text-sm">
                    Aucun thème pour le moment. {isAdmin ? (
                        <button className="text-indigo-600 underline" onClick={() => setThemeMgrOpen(true)}>Créez-en un pour commencer.</button>
                    ) : "Un admin doit en créer."}
                </CardContent></Card>
            ) : (
                <>
                    {(() => {
                        // Aplatir tous les projets, filtrer par chips (si actives), garder theme_id -> theme lookup
                        const themeById = Object.fromEntries(themes.map((t) => [t.id, t]));
                        const allProjects = projects
                            .filter((p) => !search || (p.name || '').toLowerCase().includes(search.toLowerCase()))
                            .filter((p) => themeFilters.size === 0 || themeFilters.has(p.theme_id));
                        if (allProjects.length === 0) {
                            return (
                                <Card><CardContent className="p-8 text-center text-sm text-slate-400" data-testid="grid-empty">
                                    {search || themeFilters.size > 0
                                        ? 'Aucun projet ne correspond aux filtres actifs.'
                                        : "Aucun projet dans cet espace. Cliquez sur « Nouveau projet » pour commencer."}
                                </CardContent></Card>
                            );
                        }
                        return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="projects-grid">
                                {allProjects.map((p) => (
                                    <ProjectCard
                                        key={p.id}
                                        project={p}
                                        members={members}
                                        theme={themeById[p.theme_id] || null}
                                        onClick={() => navigate(`/projets/projet/${p.id}`)}
                                        onDelete={projectDeleteHandler}
                                    />
                                ))}
                            </div>
                        );
                    })()}
                </>
            )}

            {/* Vue kanban temporaire d'un thème (bouton dédié depuis la chip) */}
            {kanbanFor && (
                <Dialog open={!!kanbanFor} onOpenChange={(o) => { if (!o) setKanbanFor(null); }}>
                    <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="kanban-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <ThemeIcon icon={kanbanFor.icon} color={kanbanFor.color} className="w-5 h-5" />
                                Kanban — {kanbanFor.name}
                            </DialogTitle>
                            <DialogDescription>Projets du thème « {kanbanFor.name} » répartis par étape du pipeline.</DialogDescription>
                        </DialogHeader>
                        <div className="flex gap-3 overflow-x-auto pb-2">
                            {(kanbanFor.pipeline || []).map((stage) => {
                                const stageProjects = (projectsByTheme[kanbanFor.id] || []).filter((p) => p.current_stage_id === stage.id);
                                return (
                                    <div key={stage.id} className="min-w-[260px] w-[260px] shrink-0 flex flex-col">
                                        <div className="flex items-center justify-between mb-2 px-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                                                <span className="text-xs font-semibold text-slate-700">{stage.name}</span>
                                            </div>
                                            <Badge variant="secondary" className="text-[10px]">{stageProjects.length}</Badge>
                                        </div>
                                        <div className="flex-1 space-y-2 rounded-xl bg-slate-50/60 p-2 min-h-[160px]">
                                            {stageProjects.length === 0 ? (
                                                <div className="text-[11px] text-slate-400 text-center py-4 italic">—</div>
                                            ) : (
                                                stageProjects.map((p) => (
                                                    <ProjectCard key={p.id} project={p} members={members} theme={kanbanFor} onClick={() => { setKanbanFor(null); navigate(`/projets/projet/${p.id}`); }} onDelete={projectDeleteHandler} />
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {isAdmin && (
                <ThemeManagerDialog
                    open={themeMgrOpen}
                    onOpenChange={setThemeMgrOpen}
                    onChange={load}
                    spaceId={spaceId}
                />
            )}

            <MyTemplatesDialog
                open={myTemplatesOpen}
                onOpenChange={setMyTemplatesOpen}
                onChange={load}
                spaceId={spaceId}
            />

            {creating && (
                <CreateProjectDialog
                    open={creating}
                    onOpenChange={(o) => { setCreating(o); if (!o) setPreselectThemeId(null); }}
                    themes={themes}
                    members={members}
                    spaceId={spaceId}
                    onCreated={() => { setCreating(false); setPreselectThemeId(null); load(); }}
                    initialThemeId={preselectThemeId || (themes[0]?.id ?? null)}
                />
            )}
            {capBlocked && (
                <CreationCapBlockedDialog
                    open={!!capBlocked}
                    onOpenChange={(o) => { if (!o) setCapBlocked(null); }}
                    limitInfo={capBlocked}
                    onRequested={() => setCapBlocked(null)}
                />
            )}

            {/* Confirmation suppression de projet — admin_full uniquement */}
            <AlertDialog open={!!projectToDelete} onOpenChange={(o) => !o && setProjectToDelete(null)}>
                <AlertDialogContent data-testid="delete-project-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer le projet « {projectToDelete?.name} » ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action supprime le projet et TOUS ses éléments liés : tâches, murs, commentaires, réactions et historique d&apos;activité. Elle est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel disabled={deletingProject} data-testid="delete-project-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleDeleteProject(); }}
                            disabled={deletingProject}
                            data-testid="delete-project-confirm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {deletingProject ? 'Suppression…' : 'Supprimer'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
        </DashboardLayout>
    );
}

// Section dépliable par thème : affiche le kanban propre au thème + entête (icône, nom, count, actions).
function ThemeSection({ theme, projects, members, isAdmin, canCreate, defaultOpen, onCreateProject, onProjectClick, onProjectDelete, onThemeSaved }) {
    const [open, setOpen] = useState(defaultOpen);
    const [pipelineOpen, setPipelineOpen] = useState(false);
    const stages = theme.pipeline || [];
    return (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`theme-section-${theme.id}`}>
            {/* En-tête du thème */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
                <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`theme-section-toggle-${theme.id}`}>
                    <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0"
                        style={{ backgroundColor: `${theme.color}20`, color: theme.color }}
                    >
                        {theme.icon || '📁'}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                            {theme.name}
                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">
                                {projects.length} projet{projects.length > 1 ? 's' : ''}
                            </Badge>
                        </div>
                        {theme.description && (
                            <div className="text-[11px] text-slate-500 truncate">{theme.description}</div>
                        )}
                    </div>
                </button>
                <div className="flex gap-1 items-center">
                    {isAdmin && (
                        <Hint text="Modifier les étapes du pipeline de ce thème (nombre, noms, couleurs, délais max par étape).">
                            <Button size="sm" variant="ghost" onClick={() => setPipelineOpen(true)} className="gap-1 text-xs" data-testid={`btn-edit-theme-pipeline-${theme.id}`}>
                                <Route className="w-3.5 h-3.5" /> Pipeline
                            </Button>
                        </Hint>
                    )}
                    {canCreate && (
                        <Hint text={`Créer un nouveau projet directement dans le thème « ${theme.name} ».`}>
                            <Button size="sm" onClick={onCreateProject} className="gap-1 text-xs" data-testid={`btn-new-in-theme-${theme.id}`}>
                                <Plus className="w-3.5 h-3.5" /> Nouveau
                            </Button>
                        </Hint>
                    )}
                </div>
            </div>

            {/* Kanban propre au thème */}
            {open && (
                <div className="p-3">
                    <div className="flex gap-3 overflow-x-auto pb-2" data-testid={`kanban-${theme.id}`}>
                        {stages.map((stage) => {
                            const stageProjects = projects.filter((p) => p.current_stage_id === stage.id);
                            return (
                                <div key={stage.id} className="min-w-[260px] w-[260px] shrink-0 flex flex-col">
                                    <div className="flex items-center justify-between mb-2 px-2">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                                            <span className="text-xs font-semibold text-slate-700">{stage.name}</span>
                                        </div>
                                        <Badge variant="secondary" className="text-[10px]">{stageProjects.length}</Badge>
                                    </div>
                                    <div className="flex-1 space-y-2 rounded-xl bg-slate-50/60 p-2 min-h-[160px]">
                                        {stageProjects.length === 0 ? (
                                            <div className="text-[11px] text-slate-400 text-center py-4 italic">—</div>
                                        ) : (
                                            stageProjects.map((p) => (
                                                <ProjectCard key={p.id} project={p} members={members} theme={theme} onClick={() => onProjectClick(p)} onDelete={onProjectDelete} />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {pipelineOpen && (
                <PipelineEditorDialog
                    open={pipelineOpen}
                    onOpenChange={setPipelineOpen}
                    theme={theme}
                    onSaved={(updated) => { onThemeSaved?.(updated); setPipelineOpen(false); }}
                />
            )}
        </div>
    );
}


function ProjectCard({ project, members, theme, onClick, onDelete }) {
    const days = daysLeft(project.deadline);
    const overdue = project.status === 'active' && days != null && days < 0;
    const soon = project.status === 'active' && days != null && days >= 0 && days <= 3;
    const imp = importanceColor(project.importance || 0);
    const memberInfo = (project.members || []).slice(0, 3).map((mid) => members.find((m) => m.id === mid)).filter(Boolean);
    const extras = Math.max(0, (project.members?.length || 0) - memberInfo.length);
    const isClosed = project.status === 'won' || project.status === 'lost';
    const stageTime = computeStageTimeliness(project, theme);
    const stageBlocked = stageTime && stageTime.over_days > 0 && !isClosed;
    const stageSoon = stageTime && !stageBlocked && stageTime.days_left_on_stage >= 0 && stageTime.days_left_on_stage <= 2 && !isClosed;

    return (
        <div className="relative group">
        <Hint text={`Ouvrir ${project.name}${project.goal ? ' — But: ' + project.goal : ''}`} delay={400}>
            <button
                onClick={onClick}
                data-testid={`project-card-${project.id}`}
                className={cn(
                    "w-full text-left rounded-lg border bg-white p-3 hover:shadow-md transition-all",
                    stageBlocked ? "border-red-300 hover:border-red-400" : "border-slate-200 hover:border-indigo-300"
                )}
            >
            <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="text-sm font-semibold text-slate-900 line-clamp-2 flex-1 flex items-start gap-1.5">
                    {project.has_unread && (
                        <span
                            className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0 animate-pulse"
                            title="Nouveau message non lu"
                            data-testid={`project-unread-${project.id}`}
                        />
                    )}
                    <span>{project.name}</span>
                </div>
                {isClosed && (
                    project.status === 'won'
                        ? <Trophy className="w-4 h-4 text-emerald-500 shrink-0" />
                        : <Skull className="w-4 h-4 text-slate-500 shrink-0" />
                )}
            </div>
            {/* Chip thème + étape courante — identification claire */}
            {theme && (
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                    <span
                        className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 font-medium"
                        style={{ backgroundColor: `${theme.color}20`, color: theme.color }}
                        data-testid={`project-theme-chip-${project.id}`}
                    >
                        <ThemeIcon icon={theme.icon} className="w-3 h-3" color={theme.color} />
                        {theme.name}
                    </span>
                    {(() => {
                        const stage = (project.pipeline || theme.pipeline || []).find((s) => s.id === project.current_stage_id);
                        return stage ? (
                            <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 bg-slate-100 text-slate-700">
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stage.color }} />
                                {stage.name}
                            </span>
                        ) : null;
                    })()}
                </div>
            )}
            {project.goal && <div className="text-[11px] text-slate-500 italic line-clamp-2 mb-2">🎯 {project.goal}</div>}
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
                <span className={cn("inline-flex items-center gap-1 text-[10px] rounded-full px-1.5 py-0.5 font-medium", imp.bg, imp.text)}>
                    <Flame className="w-3 h-3" /> {project.importance}/10
                </span>
                {overdue && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-600 font-medium" title="Deadline globale passée — info seule, ne signifie pas « en retard » sur l'étape.">Deadline passée +{Math.abs(days)}j</span>}
                {soon && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-amber-100 text-amber-700 font-medium">Deadline J-{days}</span>}
                {stageBlocked && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-red-100 text-red-700 font-semibold" title={`Étape actuelle : max ${stageTime.max_days}j — dépassée de ${stageTime.over_days}j`}>⚠ En retard +{stageTime.over_days}j</span>}
                {stageSoon && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-orange-100 text-orange-700 font-medium">Étape J-{stageTime.days_left_on_stage}</span>}
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {project.deadline ? new Date(project.deadline).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'}</span>
                <span className="flex items-center gap-1">
                    <Users2 className="w-3 h-3" />
                    <span className="flex -space-x-1">
                        {memberInfo.map((m) => (
                            <span key={m.id} className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[8px] flex items-center justify-center border border-white font-semibold" title={`${m.first_name} ${m.last_name}`}>
                                {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                            </span>
                        ))}
                        {extras > 0 && <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-600 text-[8px] flex items-center justify-center border border-white font-semibold">+{extras}</span>}
                    </span>
                </span>
            </div>
            {project.last_wall_message && (
                <div
                    className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[10px] text-slate-500"
                    data-testid={`project-last-message-${project.id}`}
                    title={project.last_wall_message.preview}
                >
                    <span
                        className="w-4 h-4 rounded-full bg-slate-200 text-slate-700 text-[8px] flex items-center justify-center font-semibold shrink-0"
                        title={project.last_wall_message.author
                            ? `${project.last_wall_message.author.first_name || ''} ${project.last_wall_message.author.last_name || ''}`.trim()
                            : ''}
                    >
                        {(project.last_wall_message.author?.first_name?.[0] || '?')}
                        {(project.last_wall_message.author?.last_name?.[0] || '')}
                    </span>
                    <MessageSquare className="w-3 h-3 shrink-0 text-slate-400" />
                    <span className="truncate flex-1">{project.last_wall_message.preview}</span>
                    <span className="text-[9px] text-slate-400 shrink-0">
                        {formatRelativeShort(project.last_wall_message.created_at)}
                    </span>
                </div>
            )}
        </button>
        </Hint>
        {/* Corbeille admin_full — apparaît au hover en absolu pour ne pas gêner le kanban */}
        {onDelete && (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(project); }}
                className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded-md bg-white/95 hover:bg-red-50 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 shadow-sm z-10"
                title="Supprimer ce projet"
                data-testid={`project-delete-btn-${project.id}`}
            >
                <Trash2 className="w-3.5 h-3.5" />
            </button>
        )}
        </div>
    );
}

function CreateProjectDialog({ open, onOpenChange, themes, members, spaceId, onCreated, initialThemeId }) {
    const [form, setForm] = useState({
        name: '',
        theme_id: initialThemeId || themes[0]?.id || '',
        description: '',
        goal: '',
        importance: 5,
        deadline: '',
        members: [],
    });
    const [submitting, setSubmitting] = useState(false);

    const selectedTheme = useMemo(() => themes.find((t) => t.id === form.theme_id), [themes, form.theme_id]);
    // Deadline auto-calculée à partir de la somme des délais du pipeline
    const totalPipelineDays = useMemo(() => {
        const p = selectedTheme?.pipeline || [];
        return p.reduce((n, s) => n + (Number(s.max_days) || 0), 0);
    }, [selectedTheme]);
    const autoDeadlineISO = useMemo(() => {
        if (!totalPipelineDays) return '';
        return new Date(Date.now() + totalPipelineDays * 86400000).toISOString().slice(0, 10);
    }, [totalPipelineDays]);

    const submit = async () => {
        if (!form.name.trim()) { toast.error('Nom obligatoire'); return; }
        if (!form.theme_id) { toast.error('Choisissez un thème'); return; }
        // Deadline auto-générée depuis le pipeline. Elle sera modifiable plus tard.
        const deadlineISO = autoDeadlineISO ? new Date(autoDeadlineISO).toISOString() : null;
        setSubmitting(true);
        try {
            await hubAPI.createProject(spaceId, {
                ...form,
                deadline: deadlineISO,
            });
            toast.success('Projet créé');
            onCreated();
        } catch (e) {
            const err = e.response?.data?.detail;
            if (typeof err === 'object' && err?.code === 'PROJECT_CAP_REACHED') {
                toast.error(err.message);
            } else {
                toast.error(err || 'Erreur');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const toggleMember = (id) => {
        setForm((f) => ({
            ...f,
            members: f.members.includes(id) ? f.members.filter((m) => m !== id) : [...f.members, id],
        }));
    };
    const selectAll = () => setForm((f) => ({ ...f, members: members.map((m) => m.id) }));
    const clearAll = () => setForm((f) => ({ ...f, members: [] }));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="create-project-dialog">
                <DialogHeader>
                    <DialogTitle>Nouveau projet</DialogTitle>
                    <DialogDescription>Un projet a une importance, une date butoir et un but final.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="flex items-center gap-1.5">
                            Nom du projet *
                            <InfoHint text="Nom court et clair (ex: code de la fiche CEE, titre du dispositif). Sera visible sur toutes les cartes kanban." />
                        </Label>
                        <Input data-testid="np-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : IND-UT-116 Éclairage LED" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="flex items-center gap-1.5">
                                Thème *
                                <InfoHint text="Le thème détermine le pipeline (les colonnes du kanban) et donc la durée totale du projet. Chaque étape a un délai max — la date butoir est calculée automatiquement." />
                            </Label>
                            <select
                                value={form.theme_id}
                                onChange={(e) => setForm({ ...form, theme_id: e.target.value })}
                                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                                data-testid="np-theme"
                            >
                                {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="flex items-center gap-1.5">
                                Date butoir (auto)
                                <InfoHint text="Calculée à partir de la somme des délais des étapes du pipeline du thème. Modifiable après création — un changement de date rescale proportionnellement les étapes." />
                            </Label>
                            <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700" data-testid="np-deadline-auto">
                                {autoDeadlineISO
                                    ? `${new Date(autoDeadlineISO).toLocaleDateString('fr-FR')} · ${totalPipelineDays}j`
                                    : (totalPipelineDays === 0 ? 'Pipeline sans délai' : '—')}
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label className="flex items-center gap-1.5">
                            Importance : <span className="font-semibold text-indigo-600">{form.importance}/10</span>
                            <InfoHint text="0-2 : basse. 3-4 : moyenne. 5-7 : haute. 8-10 : critique. Sert à trier et à colorer visuellement les cartes." />
                        </Label>
                        <input
                            type="range" min="0" max="10" value={form.importance}
                            onChange={(e) => setForm({ ...form, importance: parseInt(e.target.value) })}
                            className="w-full mt-2"
                            data-testid="np-importance"
                        />
                    </div>
                    <div>
                        <Label className="flex items-center gap-1.5">
                            But final
                            <InfoHint text="Qu'est-ce qui définira le succès ? Ce texte s'affiche en haut du projet et sert de rappel à toute l'équipe. Ex: « Signer 3 fiches CEE avec l'industrie X. »" />
                        </Label>
                        <Textarea data-testid="np-goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} rows={2} placeholder="Qu'est-ce qui définira le succès ?" />
                    </div>
                    <div>
                        <Label>Description (optionnel)</Label>
                        <Textarea data-testid="np-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <Label className="flex items-center gap-1.5">
                                Membres impliqués ({form.members.length})
                                <InfoHint text="Personnes qui participent au projet. Ne sont listés ici que les utilisateurs ayant un accès Hub. Vous êtes ajouté automatiquement en tant que propriétaire." />
                            </Label>
                            <div className="flex gap-1">
                                <Button type="button" size="sm" variant="ghost" onClick={selectAll} className="h-6 text-[10px]">Tous</Button>
                                <Button type="button" size="sm" variant="ghost" onClick={clearAll} className="h-6 text-[10px]">Aucun</Button>
                            </div>
                        </div>
                        <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2 space-y-1">
                            {members.length === 0 ? (
                                <div className="text-xs text-slate-400 italic p-2">Aucun utilisateur avec accès Hub</div>
                            ) : members.map((m) => (
                                <label key={m.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                                    <Checkbox checked={form.members.includes(m.id)} onCheckedChange={() => toggleMember(m.id)} data-testid={`np-member-${m.id}`} />
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-[10px] flex items-center justify-center font-semibold">
                                        {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                                    </span>
                                    <span className="text-xs text-slate-700 flex-1">{m.first_name} {m.last_name}</span>
                                    <span className="text-[9px] text-slate-400">{m.role}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={submitting} data-testid="np-submit">{submitting ? 'Création…' : 'Créer le projet'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Encart "À propos de cet espace" — visible par tous, éditable inline par les admins
function SpaceDescriptionCard({ space, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(space?.description || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(space?.description || ''); }, [space?.description]);

    const submit = async () => {
        setSaving(true);
        try {
            const r = await hubAPI.updateSpace(space.id, { description: draft });
            toast.success('Description mise à jour');
            onSaved?.(r.data);
            setEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Enregistrement impossible');
        } finally {
            setSaving(false);
        }
    };

    const hasContent = (space?.description || '').trim().length > 0;

    // Rien à afficher pour non-admin sans description
    if (!hasContent && !canEdit) return null;

    return (
        <div
            className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white p-4"
            data-testid="space-description-card"
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                    <Info className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-slate-800">À propos de cet espace</h3>
                        {canEdit && !editing && (
                            <button
                                onClick={() => setEditing(true)}
                                className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                data-testid="space-desc-edit"
                            >
                                <Pencil className="w-3 h-3" /> {hasContent ? 'Modifier' : 'Ajouter une description'}
                            </button>
                        )}
                    </div>
                    {editing ? (
                        <div className="space-y-2" data-testid="space-desc-edit-form">
                            <Textarea
                                autoFocus
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') { setEditing(false); setDraft(space?.description || ''); }
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
                                }}
                                rows={5}
                                placeholder={"Ex: Espace dédié aux projets Fiche CEE (procédure Fiche standardisée).\n\nRègles :\n- Nom du projet = référence Fiche + site\n- Chaque projet doit avoir un binôme (dev + technique)\n- Passage en Décision Go/No-Go = obligation de joindre l'étude de faisabilité"}
                                className="text-sm resize-y"
                                data-testid="space-desc-textarea"
                            />
                            <div className="flex items-center gap-2 text-[11px]">
                                <Button size="sm" onClick={submit} disabled={saving} className="gap-1" data-testid="space-desc-save">
                                    <Check className="w-3.5 h-3.5" /> {saving ? '…' : 'Enregistrer'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(space?.description || ''); }} disabled={saving} className="gap-1">
                                    <X className="w-3.5 h-3.5" /> Annuler
                                </Button>
                                <span className="text-slate-400 ml-auto hidden sm:inline">Ctrl+Entrée = valider · Échap = annuler</span>
                            </div>
                        </div>
                    ) : hasContent ? (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="space-desc-text">
                            {space.description}
                        </p>
                    ) : (
                        <p className="text-xs text-slate-400 italic">Cet espace n&apos;a pas encore d&apos;explication. Ajoutez-en une pour que tous les membres sachent à quoi il sert et quelles règles suivre.</p>
                    )}
                </div>
            </div>
        </div>
    );
}



// Petit encart de description du thème actif (avec édition inline + éditeur pipeline pour l'admin)
function ThemeDescriptionCard({ theme, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(theme?.description || '');
    const [saving, setSaving] = useState(false);
    const [pipelineOpen, setPipelineOpen] = useState(false);

    useEffect(() => { setDraft(theme?.description || ''); }, [theme?.id, theme?.description]);

    const submit = async () => {
        setSaving(true);
        try {
            const r = await hubAPI.updateTheme(theme.id, { description: draft });
            toast.success('Description du thème mise à jour');
            onSaved?.(r.data);
            setEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Enregistrement impossible');
        } finally {
            setSaving(false);
        }
    };

    const hasContent = (theme?.description || '').trim().length > 0;
    if (!hasContent && !canEdit) return null;

    return (
        <div
            className="rounded-lg border p-3 flex items-start gap-3"
            style={{ borderColor: `${theme.color}55`, backgroundColor: `${theme.color}0D` }}
            data-testid={`theme-description-${theme.id}`}
        >
            <span
                className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                style={{ backgroundColor: `${theme.color}22` }}
            >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.color }} />
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-700">Thème « {theme.name} » · {(theme.pipeline || []).length} étape{(theme.pipeline || []).length > 1 ? 's' : ''}</div>
                    {canEdit && !editing && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPipelineOpen(true)}
                                className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                data-testid={`theme-pipeline-edit-${theme.id}`}
                                title="Ajouter, réordonner ou modifier les étapes du pipeline"
                            >
                                <Route className="w-3 h-3" /> Modifier le pipeline
                            </button>
                            <span className="text-slate-300">·</span>
                            <button
                                onClick={() => setEditing(true)}
                                className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                data-testid={`theme-desc-edit-${theme.id}`}
                            >
                                <Pencil className="w-3 h-3" /> {hasContent ? 'Modifier' : 'Ajouter'} la description
                            </button>
                        </div>
                    )}
                </div>
                {editing ? (
                    <div className="mt-1 space-y-2">
                        <Textarea
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') { setEditing(false); setDraft(theme?.description || ''); }
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
                            }}
                            rows={3}
                            placeholder={`Ex: À utiliser pour tout projet ${theme.name} - précisez ici les cas d'usage et règles internes.`}
                            className="text-sm resize-y"
                            data-testid={`theme-desc-textarea-${theme.id}`}
                        />
                        <div className="flex items-center gap-2 text-[11px]">
                            <Button size="sm" onClick={submit} disabled={saving} className="gap-1" data-testid={`theme-desc-save-${theme.id}`}>
                                <Check className="w-3.5 h-3.5" /> {saving ? '…' : 'Enregistrer'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(theme?.description || ''); }} disabled={saving} className="gap-1">
                                <X className="w-3.5 h-3.5" /> Annuler
                            </Button>
                        </div>
                    </div>
                ) : hasContent ? (
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed whitespace-pre-wrap">{theme.description}</p>
                ) : (
                    <p className="text-[11px] text-slate-400 italic mt-0.5">Aucune description. Ajoutez-en une pour aider votre équipe à choisir le bon thème.</p>
                )}
            </div>
            <PipelineEditorDialog
                open={pipelineOpen}
                onOpenChange={setPipelineOpen}
                theme={theme}
                onSaved={(updatedTheme) => onSaved?.(updatedTheme)}
            />
        </div>
    );
}

